/**
 * @file Composition root.
 *
 * The single place where concrete implementations are chosen and wired
 * together. Every other module receives what it needs through the context, so
 * this file is the only one that knows the whole graph — and the only one that
 * has to change when a service is swapped.
 */

import { CONFIG } from '../config/GameConfig.js';
import { EventBus, Events } from './EventBus.js';
import { Engine } from './Engine.js';
import { SceneManager } from './SceneManager.js';
import { AssetLoader } from './AssetLoader.js';
import { World } from '../ecs/World.js';
import { createSandboxManager } from '../sandbox/SandboxManager.js';
import { Analytics } from '../analytics/Analytics.js';
import { AnalyticsBridge } from '../analytics/AnalyticsBridge.js';

import { Renderer } from '../rendering/Renderer.js';
import { CameraController } from '../rendering/CameraController.js';
import { Lighting } from '../rendering/Lighting.js';
import { MeshFactory } from '../rendering/MeshFactory.js';
import { registerPrefabs } from '../rendering/Prefabs.js';
import { WeaponModelLibrary } from '../rendering/WeaponModels.js';
import { CharacterModelLibrary } from '../rendering/CharacterModels.js';

import { InputManager, Action } from '../input/InputManager.js';
import { InputMode, resolveInputMode } from '../input/deviceProfile.js';
import { TouchControls } from '../ui/TouchControls.js';
import { AudioManager } from '../audio/AudioManager.js';
import { MusicDirector } from '../audio/MusicDirector.js';
import { HUD } from '../ui/HUD.js';
import { WaveDebugPanel } from '../ui/WaveDebugPanel.js';
import { DungeonDebugPanel } from '../ui/DungeonDebugPanel.js';
import { DebugPanel } from '../ui/DebugPanel.js';
import { LevelLabPanel } from '../ui/LevelLabPanel.js';
import { PostFxPanel } from '../ui/PostFxPanel.js';
import { loadDebugPrefs } from '../config/DebugPrefs.js';
import { loadWaveTuning } from '../config/WaveTuning.js';
import { loadPlayerTuning } from '../config/PlayerTuning.js';
import { loadWeaponTuning } from '../config/WeaponTuning.js';
import { loadEnemyTuning } from '../config/EnemyTuning.js';
import { loadCameraTuning } from '../config/CameraTuning.js';

import { PostProcessing } from '../rendering/PostProcessing.js';

import { StatusEffectSystem } from '../systems/StatusEffectSystem.js';
import { PlayerControlSystem } from '../systems/PlayerControlSystem.js';
import { DashSystem } from '../systems/DashSystem.js';
import { ThrowSystem } from '../systems/ThrowSystem.js';
import { EnemyAISystem } from '../systems/EnemyAISystem.js';
import { EnemyRangedAISystem } from '../systems/EnemyRangedAISystem.js';
import { WeaponSystem } from '../systems/WeaponSystem.js';
import { MeleeWeaponSystem } from '../systems/MeleeWeaponSystem.js';
import { CombatSystem } from '../systems/CombatSystem.js';
import { StreamFlameSystem } from '../systems/StreamFlameSystem.js';
import { MovementSystem } from '../systems/MovementSystem.js';
import { FallbackWeaponSystem } from '../systems/FallbackWeaponSystem.js';
import { KnockbackSystem } from '../systems/KnockbackSystem.js';
import { CollisionSystem } from '../systems/CollisionSystem.js';
import { ThrownWeaponSystem } from '../systems/ThrownWeaponSystem.js';
import { PickupSystem } from '../systems/PickupSystem.js';
import { WeaponDropSystem } from '../systems/WeaponDropSystem.js';
import { InteractionSystem } from '../systems/InteractionSystem.js';
import { LifetimeSystem } from '../systems/LifetimeSystem.js';
import { BurnSystem } from '../systems/BurnSystem.js';
import { HealthSystem } from '../systems/HealthSystem.js';
import { ExplosionSystem } from '../systems/ExplosionSystem.js';
import { WaveSystem } from '../systems/WaveSystem.js';
import { ParticleSystem } from '../systems/ParticleSystem.js';
import { TrailSystem } from '../systems/TrailSystem.js';
import { LightFxSystem } from '../systems/LightFxSystem.js';
import { CameraShakeSystem } from '../systems/CameraShakeSystem.js';
import { SniperLaserSystem } from '../systems/SniperLaserSystem.js';
import { VisualIndicatorSystem } from '../systems/VisualIndicatorSystem.js';
import { WallCutoutSystem } from '../systems/WallCutoutSystem.js';
import { MusicCueSystem } from '../systems/MusicCueSystem.js';
import { RenderSystem } from '../systems/RenderSystem.js';
import { CharacterAnimationSystem } from '../systems/CharacterAnimationSystem.js';
import { HeldWeaponSystem } from '../systems/HeldWeaponSystem.js';
import { DebugOverlaySystem } from '../systems/DebugOverlaySystem.js';
import { ExitDirectionSystem } from '../systems/ExitDirectionSystem.js';

import { StateMachine, GameStates } from './states/StateMachine.js';
import { MenuState } from './states/MenuState.js';
import { SplashState } from './states/SplashState.js';
import { LoadingState } from './states/LoadingState.js';
import { PlayingState } from './states/PlayingState.js';
import { PausedState } from './states/PausedState.js';
import { GameOverState } from './states/GameOverState.js';

import { teardownLevel } from '../world/Level.js';

/**
 * Camera pull-back on touch. "Reduce zoom by 50%" means the view covers twice
 * the ground, which is a *larger* distance — halving `distance` would be the
 * opposite, a closer camera.
 */
const TOUCH_ZOOM_OUT_FACTOR = 1.5;

export class Game {
  /**
   * @param {object} options Setup.
   * @param {HTMLCanvasElement} options.canvas Render target.
   * @param {HTMLElement} options.ui HUD container.
   * @param {import('../config/GameConfig.js').GameConfig} [options.config] Config override.
   */
  constructor({ canvas, ui, config = CONFIG }) {
    /** @type {HTMLCanvasElement} @private */
    this._canvas = canvas;
    /** @type {HTMLElement} @private */
    this._ui = ui;
    const debugTools = Boolean(import.meta.env?.DEV || config.debug.waveInspector);
    if (debugTools) loadCameraTuning();

    const events = new EventBus();
    const sceneManager = new SceneManager(config);
    const renderer = new Renderer(canvas, config);
    // Decided once, at boot, and carried on the context: everything that has to
    // branch on it (the HUD legend, the on-screen pad, the camera below) reads
    // `ctx.inputMode`.
    const inputMode = resolveInputMode();

    const cameraController = new CameraController(config, renderer.aspect);
    if (inputMode === InputMode.TOUCH) {
      // Thumbs sit over the bottom third of the screen, so a distance tuned for
      // a mouse-free view reads as "zoomed in" the moment the pad covers it —
      // half the arena a desktop player can see is now under a thumb. Pull the
      // rig back rather than touch the shared tuning: the wheel that would
      // normally let a player correct this is deliberately absent on touch
      // (touch-action: none owns that gesture for the sticks instead).
      cameraController.distance *= TOUCH_ZOOM_OUT_FACTOR;
      // The constructor already placed the camera from the pre-multiply
      // distance; re-run that placement now or the rig stays at the old
      // distance until the first PLAYING frame calls update().
      cameraController.snapTo(cameraController.pivot);
    }
    const lighting = new Lighting(sceneManager.scene, config);
    const meshFactory = new MeshFactory();
    const weaponModels = new WeaponModelLibrary();
    const characterModels = new CharacterModelLibrary();
    const input = new InputManager(canvas);
    input.touchMode = inputMode === InputMode.TOUCH;
    const audio = new AudioManager(events, config);
    const world = new World(events);
    const analytics = new Analytics();

    /** @type {import('./Context.js').TimeInfo} */
    const time = { delta: 0, elapsed: 0, fps: 0, frame: 0 };

    /** @type {import('./Context.js').GameContext} */
    this.ctx = {
      config,
      events,
      world,
      scene: sceneManager.scene,
      renderer,
      cameraController,
      meshFactory,
      weaponModels,
      characterModels,
      input,
      inputMode,
      audio,
      analytics,
      // Set immediately below: MusicDirector reads `ctx.levelIndex` live on
      // every transition, so it needs the real, already-populated context
      // rather than a snapshot of it.
      music: null,
      lighting,
      time,
      arena: null,
      player: null,
      runSeed: null,
      levelIndex: 1,
      levelSeed: null,
      playerWeaponKey: undefined
    };
    this.ctx.music = new MusicDirector(this.ctx);

    /** @type {SceneManager} @private */
    this._sceneManager = sceneManager;
    /** @type {AssetLoader} @private */
    this._assets = new AssetLoader();
    /**
     * Whether this run has developer tools at all. Dev builds always do; a
     * production build only does when the debug flag is on, so a shipped game
     * never loads persisted balance overrides.
     * @type {boolean}
     * @private
     */
    this._debugTools = debugTools;
    const debugPrefs = this._debugTools ? loadDebugPrefs() : null;

    /** @type {HUD} @private */
    this._hud = new HUD(ui, events, config, {
      audio,
      music: this.ctx.music,
      debugTools: this._debugTools,
      inputMode
    });
    this._hud.restoreScales(debugPrefs?.ui);

    /**
     * On-screen sticks and buttons. Only built where they are the control
     * scheme — a desktop run never puts them in the DOM at all.
     * @type {TouchControls | null}
     * @private
     */
    this._touch = inputMode === InputMode.TOUCH ? new TouchControls(ui, this.ctx) : null;

    /**
     * The developer window (`*`) and everything docked inside it. Null when
     * this run has no debug tools (issue #56).
     * @type {DebugPanel | null}
     * @private
     */
    this._debug = null;
    /** @type {ReturnType<typeof createSandboxManager> | null} @private */
    this._sandbox = null;
    if (this._debugTools) {
      loadWaveTuning();
      loadPlayerTuning();
      loadWeaponTuning();
      loadEnemyTuning();
      this._debug = new DebugPanel(ui, this.ctx, {
        onRestartLevel: () => this.restartLevel(),
        canRestartLevel: () => this.canRestartLevel()
      });
      this._sandbox = createSandboxManager(this.ctx, canvas, this._debug.sandboxHost);
      this._debug.attach({
        waveDebug: new WaveDebugPanel(this._debug.waveHost, { chrome: false }),
        sandbox: this._sandbox,
        levelLab: new LevelLabPanel(this._debug.levelsHost, this.ctx),
        postFx: new PostFxPanel(this._debug.postfxHost, this.ctx),
        hud: this._hud
      });
    }

    /**
     * Dungeon population sandbox (`-`). Same guard as the wave sandbox: dev
     * builds always get it, a production build only when the flag is on. Null
     * means the tool does not exist this run.
     * @type {DungeonDebugPanel | null}
     * @private
     */
    this._dungeonDebug = null;
    if (import.meta.env?.DEV || config.debug.dungeonInspector) {
      this._dungeonDebug = new DungeonDebugPanel(ui);
    }

    world.setContext(this.ctx);
    registerPrefabs(meshFactory, config, weaponModels, characterModels);
    this._registerSystems();
    this._sandbox?.register();

    // Debug prefs survive the reload: reopen the window on its tab and
    // re-apply the overlay switches exactly as the last session left them.
    if (debugPrefs?.overlays) {
      if (debugPrefs.overlays.bombers !== undefined) {
        world.getSystem(DebugOverlaySystem)?.setDebugVisible(debugPrefs.overlays.bombers);
      }
      if (debugPrefs.overlays.ranged !== undefined) {
        world.getSystem(EnemyRangedAISystem)?.setDebugVisible(debugPrefs.overlays.ranged);
      }
    }
    this._debug?.restore(debugPrefs?.hub);

    /**
     * Reporting. Subscribed before the first state transition, so `splash` is
     * the first thing PostHog hears about rather than whatever the player did
     * after the title screen. The SDK itself arrives asynchronously; events
     * captured in the meantime queue inside `Analytics`.
     * @type {AnalyticsBridge}
     * @private
     */
    this._analyticsBridge = new AnalyticsBridge(this.ctx, analytics);
    this._analyticsBridge.attach();
    void analytics.init();

    /** @type {StateMachine} @private */
    this._states = new StateMachine(events);
    this._registerStates();

    /** @type {Engine} @private */
    this._engine = new Engine({
      update: (dt) => this._update(dt),
      render: () => this._render(),
      time,
      maxDelta: config.engine.maxDelta,
      fpsSampleWindow: config.engine.fpsSampleWindow
    });

    /**
     * Post chain. Null when disabled, in which case the game draws straight
     * through the renderer — the only branch in the render path.
     * @type {PostProcessing | null}
     * @private
     */
    this._post = config.post.enabled
      ? new PostProcessing(renderer, sceneManager.scene, cameraController.camera, config)
      : null;

    /** @type {() => void} @private */
    this._onResize = () => cameraController.setAspect(renderer.aspect);
    window.addEventListener('resize', this._onResize);
  }

  /**
   * Registers the system pipeline. Execution order comes from each system's
   * static `priority`, not from this list.
   * @returns {void}
   * @private
   */
  _registerSystems() {
    const { world } = this.ctx;
    // Simulation. Order of add is free: each system declares its own priority.
    world.addSystem(new StatusEffectSystem());
    world.addSystem(new PlayerControlSystem());
    world.addSystem(new DashSystem());
    world.addSystem(new ThrowSystem());
    world.addSystem(new EnemyAISystem());
    world.addSystem(new EnemyRangedAISystem());
    // Before the weapon system: a thrown weapon is replaced the same frame.
    world.addSystem(new FallbackWeaponSystem());
    world.addSystem(new WeaponSystem());
    world.addSystem(new MeleeWeaponSystem());
    world.addSystem(new CombatSystem());
    world.addSystem(new StreamFlameSystem());
    world.addSystem(new KnockbackSystem());
    world.addSystem(new MovementSystem());
    world.addSystem(new CollisionSystem());
    world.addSystem(new ThrownWeaponSystem());
    world.addSystem(new PickupSystem());
    world.addSystem(new WeaponDropSystem());
    world.addSystem(new InteractionSystem());
    world.addSystem(new LifetimeSystem());
    world.addSystem(new BurnSystem());
    world.addSystem(new HealthSystem());
    world.addSystem(new ExplosionSystem());
    world.addSystem(new WaveSystem());
    // Presentation.
    world.addSystem(new ParticleSystem());
    world.addSystem(new TrailSystem());
    world.addSystem(new LightFxSystem());
    world.addSystem(new CameraShakeSystem());
    world.addSystem(new SniperLaserSystem());
    world.addSystem(new VisualIndicatorSystem());
    world.addSystem(new WallCutoutSystem());
    world.addSystem(new MusicCueSystem());
    world.addSystem(new RenderSystem());
    world.addSystem(new CharacterAnimationSystem());
    world.addSystem(new HeldWeaponSystem());
    world.addSystem(new DebugOverlaySystem());
    world.addSystem(new ExitDirectionSystem());
  }

  /** @returns {void} @private */
  _registerStates() {
    const machine = this._states;
    machine.register(GameStates.SPLASH, new SplashState(this.ctx, machine));
    machine.register(GameStates.MENU, new MenuState(this.ctx, machine));
    machine.register(GameStates.LOADING, new LoadingState(this.ctx, machine, this._assets));
    machine.register(GameStates.PLAYING, new PlayingState(this.ctx, machine));
    machine.register(GameStates.PAUSED, new PausedState(this.ctx, machine));
    machine.register(GameStates.GAME_OVER, new GameOverState(this.ctx, machine));
  }

  /**
   * Boots the game into the title screen and starts the frame loop.
   * @returns {void}
   */
  start() {
    this._states.change(GameStates.SPLASH);
    this._engine.start();
  }

  /** @returns {boolean} True when a loaded run can be rebuilt in place. */
  canRestartLevel() {
    return Boolean(
      this._states &&
        this.ctx.runSeed != null &&
        this.ctx.levelIndex != null &&
        !this._states.is(GameStates.SPLASH) &&
        !this._states.is(GameStates.MENU) &&
        !this._states.is(GameStates.LOADING)
    );
  }

  /**
   * Rebuilds the current floor with the current run seed and level index.
   * @returns {void}
   */
  restartLevel() {
    if (!this.canRestartLevel()) return;
    this.ctx.input.releaseAll();
    this._states.change(GameStates.LOADING);
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   * @private
   */
  _update(dt) {
    this.ctx.input.pollGamepad();
    this._states.update(dt);
    // Dungeon presentation (liquids, particles, torch flicker) keeps animating
    // on pause; never regenerates here.
    this.ctx.arena?.update?.(dt);
    // Bar-boundary scheduling only, not gameplay evaluation (that is
    // `MusicCueSystem`, which only runs inside `world.update`): this must
    // keep ticking even while paused, since the AudioContext clock itself is
    // what's frozen then, not this call.
    this.ctx.music.update(dt);
    this._hud.update(dt, this.ctx);
    // Ticks on pause too: the frame-rate heartbeat is what makes a long pause
    // read as a gap in the data rather than as an absence of data.
    this._analyticsBridge.update(dt);
    // Sandbox counters and orphan-mesh cleanup run even while its tab is
    // hidden; the panel itself is inert unless the tab is up.
    this._sandbox?.update(dt);
    // Debug mode (comma or -): the developer window and the dungeon panel open
    // and close together on either key, so both expose every level-debugging
    // surface.
    if (this.ctx.input.justPressed(Action.TOGGLE_DEBUG) || this.ctx.input.justPressed(Action.DEBUG_DUNGEON)) {
      this._debug?.toggle();
      this._dungeonDebug?.toggle();
      this.ctx.events.emit(Events.DEBUG_TOGGLED, { open: Boolean(this._debug?.isOpen ?? this._dungeonDebug?.isOpen) });
    }
    this._debug?.update(dt, this.ctx);
    this._dungeonDebug?.update(dt, this.ctx);
    // Edge state must be cleared after every consumer has read it.
    this.ctx.input.endFrame();
  }

  /** @returns {void} @private */
  _render() {
    if (this._post) {
      this._post.render(this.ctx);
      return;
    }
    this.ctx.renderer.render(this._sceneManager.scene, this.ctx.cameraController.camera);
  }

  /**
   * Initialize the sandbox manager.
   * @returns {import('../sandbox/SandboxManager.js').ReturnType<typeof import('../sandbox/SandboxManager.js').createSandboxManager>}
   * @private
   */
  /**
   * Tears everything down. Safe to call once; the instance is unusable after.
   * @returns {void}
   */
  dispose() {
    window.removeEventListener('resize', this._onResize);
    this._engine.stop();
    this._post?.dispose();
    // Before the state machine: closing an open run is the last thing worth
    // reporting, and it needs the run state that teardown is about to discard.
    this._analyticsBridge.dispose();
    this.ctx.analytics.dispose();
    this._states.dispose();
    teardownLevel(this.ctx);
    this.ctx.world.dispose();
    // Before the HUD: it owns the same root and clears it wholesale.
    this._sandbox?.dispose();
    this._debug?.dispose();
    this._dungeonDebug?.dispose();
    this._touch?.dispose();
    this._hud.dispose();
    this.ctx.audio.dispose();
    this.ctx.music.dispose();
    this.ctx.input.dispose();
    this.ctx.lighting.dispose();
    this.ctx.meshFactory.dispose();
    this._assets.dispose();
    this._sceneManager.dispose();
    this.ctx.renderer.dispose();
  }
}
